/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

(() => {
  'use strict';
  if (window.CentecStockOrder) return;
  const gridSelector = '[data-centec-stock-grid]';
  const cardsIn = grid => Array.from(grid.children).filter(node => node.matches('product-item'));
  const soldOut = card => card.classList.contains('product-item--sold-out');
  function sortVisible(root) {
    root.querySelectorAll('.product-list__inner').forEach(grid => {
      if (grid.matches(gridSelector)) return;
      const cards = cardsIn(grid);
      const sorted = cards.filter(card => !soldOut(card)).concat(cards.filter(soldOut));
      if (cards.some((card, i) => card !== sorted[i])) sorted.forEach(card => grid.append(card));
    });
  }
  function pagePlan(page, size, available, unavailable) {
    const start = (page - 1) * size;
    const stop = Math.min(start + size, available + unavailable);
    const plans = [];
    for (const [value, base, count] of [['1', 0, available], ['0', available, unavailable]]) {
      let offset = Math.max(start, base) - base;
      const end = Math.min(stop, base + count) - base;
      while (offset < end) {
        const localPage = Math.floor(offset / size) + 1;
        const from = offset % size;
        const take = Math.min(size - from, end - offset);
        plans.push({ value, page: localPage, from, take });
        offset += take;
      }
    }
    return plans;
  }
  async function prepare(root, address, outerSignal) {
    const grid = root.querySelector(gridSelector);
    if (!grid || grid.dataset.stockReady) return;
    const url = new URL(address, location.href);
    const chosen = new Set(url.searchParams.getAll('filter.v.availability').flatMap(value => value.split(',')));
    if (chosen.size === 1 && (chosen.has('0') || chosen.has('1'))) return;
    const page = Number(grid.dataset.stockPage);
    const size = Number(grid.dataset.stockSize);
    const total = Number(grid.dataset.stockTotal);
    const available = Number(grid.dataset.stockAvailable);
    const unavailable = Number(grid.dataset.stockUnavailable);
    if (![page, size, total, available, unavailable].every(Number.isInteger) || page < 1 || size < 1 || available < 0 || unavailable < 0 || available + unavailable !== total) return;
    if (!available || !unavailable) { grid.dataset.stockReady = 'true'; return; }
    const facet = grid.closest('product-facet');
    const sectionId = facet && facet.getAttribute('section-id');
    if (!sectionId) return;
    const controller = new AbortController();
    const abort = () => controller.abort();
    if (outerSignal?.aborted) return;
    outerSignal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(abort, 8000);
    grid.setAttribute('aria-busy', 'true');
    try {
      const plan = pagePlan(page, size, available, unavailable);
      const groups = await Promise.all(plan.map(async part => {
        const request = new URL(url);
        request.searchParams.delete('sections');
        request.searchParams.set('section_id', sectionId);
        request.searchParams.set('filter.v.availability', part.value);
        request.searchParams.set('page', String(part.page));
        if (/\/search$/.test(request.pathname)) request.searchParams.set('options[unavailable_products]', 'last');
        const response = await fetch(request, { signal: controller.signal });
        if (!response.ok) throw new Error('Stock page request failed');
        const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
        const fetchedGrid = doc.querySelector(gridSelector);
        if (!fetchedGrid) throw new Error('Stock grid missing');
        const fetched = cardsIn(fetchedGrid).slice(part.from, part.from + part.take);
        if (fetched.length !== part.take || fetched.some(card => soldOut(card) !== (part.value === '0'))) throw new Error('Stock changed during request');
        return fetched;
      }));
      if (controller.signal.aborted) return;
      const cards = groups.flat();
      const identities = cards.map(card => card.querySelector('a[href*="/products/"]')?.getAttribute('href')?.split('?')[0]);
      if (identities.some(id => !id) || new Set(identities).size !== identities.length) throw new Error('Duplicate stock results');
      cards.forEach(card => card.removeAttribute('reveal'));
      const styles = Array.from(grid.children).filter(node => node.tagName === 'STYLE');
      grid.replaceChildren(...styles.slice(0, 1), ...cards);
      grid.dataset.stockReady = 'true';
    } catch (error) {
      if (error.name !== 'AbortError') console.warn('CENTEC: se conserva el listado original porque no se pudo completar el orden por disponibilidad.', error);
    } finally {
      clearTimeout(timer);
      outerSignal?.removeEventListener('abort', abort);
      grid.removeAttribute('aria-busy');
    }
  }
  window.CentecStockOrder = { prepare, pagePlan };
  function start() {
    prepare(document, location.href);
    sortVisible(document);
    let pending = false;
    new MutationObserver(records => {
      if (pending || !records.some(record => Array.from(record.addedNodes).some(node => node.nodeType === 1 && (node.matches?.('product-item, product-list') || node.querySelector?.('product-item'))))) return;
      pending = true;
      requestAnimationFrame(() => { pending = false; sortVisible(document); });
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !/\/search\/?$/.test(new URL(form.action, location.href).pathname)) return;
      let input = form.querySelector('[name="options[unavailable_products]"]');
      if (!input) { input = document.createElement('input'); input.type = 'hidden'; input.name = 'options[unavailable_products]'; form.append(input); }
      input.value = 'last';
    }, true);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
  else start();
})();

(() => {
  if (window.CentecSearch?.version === 2) return;
  const clean = value => String(value || '').normalize('NFKC').trim().replace(/\s+/gu, ' ');
  const simple = value => value && value.length <= 100 && !/["*:()\\]/u.test(value) && !/\b(?:OR|AND|NOT)\b/u.test(value);
  function variants(value) {
    const original = clean(value);
    if (!simple(original)) return [];
    const spaced = original.replace(/[\p{Pd}]+/gu, ' ').replace(/(\p{L})(\d)/gu, '$1 $2').replace(/(\d)(\p{L})/gu, '$1 $2');
    const joinBefore = text => text.replace(/(\p{L})[\s\p{Pd}]+(?=\d)/gu, '$1');
    const joinAfter = text => text.replace(/(\d)[\s\p{Pd}]+(?=\p{L})/gu, '$1');
    return [...new Set([spaced, joinBefore(original), joinAfter(original), joinAfter(joinBefore(original))].map(clean))].filter(q => q !== original);
  }
  function exactQuery(value) {
    const original = clean(value);
    if (!simple(original) || !/\d/u.test(original)) return '';
    return [original, ...variants(original)].flatMap(q => ['title', 'variants.sku', 'variants.barcode'].map(field => field + ':"' + q + '"')).join(' OR ');
  }
  const compact = value => clean(value).toLocaleLowerCase().replace(/[^\p{L}\d]/gu, '');
  function exactProduct(product, query) {
    const needle = compact(query);
    if (!needle) return false;
    if ((product.variants || []).some(variant => [variant.sku, variant.barcode].some(value => value && compact(value) === needle))) return true;
    const title = compact(product.title);
    let start = title.indexOf(needle);
    while (start >= 0) {
      const before = title[start - 1] || '';
      const after = title[start + needle.length] || '';
      if (!(/\d/u.test(needle[0]) && /\d/u.test(before)) && !(/\d/u.test(needle.at(-1)) && /\d/u.test(after))) return true;
      start = title.indexOf(needle, start + 1);
    }
    return false;
  }
  const predictiveCache = new Map();
  async function predictive(url, query, signal) {
    const key = new URL(url, location.href).pathname + ':' + query;
    const cached = predictiveCache.get(key);
    if (cached && Date.now() - cached.time < 30000) return cached.result;
    const queries = [query, ...variants(query)];
    const settled = await Promise.allSettled(queries.map(async candidate => {
      const request = new URL(url, location.href);
      request.searchParams.set('q', candidate);
      const response = await fetch(request, {signal, headers: {Accept: 'application/json'}});
      if (!response.ok) throw new Error('Search ' + response.status);
      return {data: await response.json(), query: candidate};
    }));
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const responses = settled.filter(item => item.status === 'fulfilled').map(item => item.value);
    if (!responses.length) throw new Error('Search unavailable');
    const base = responses.find(item => item.query === query) || responses[0];
    const products = [...new Map(responses.flatMap(item => item.data.resources?.results?.products || []).map(product => [product.id || product.url, product])).values()];
    const exact = /\d/u.test(query) ? products.filter(product => exactProduct(product, query)) : [];
    const selected = exact.length ? exact : (base.data.resources?.results?.products?.length ? base.data.resources.results.products : products);
    const ranked = selected.map((product, index) => ({product, index})).sort((a, b) => Number(a.product.available === false) - Number(b.product.available === false) || a.index - b.index).map(item => item.product).slice(0, 6);
    const result = {query, data: {...base.data, resources: {...base.data.resources, results: {...base.data.resources?.results, products: ranked}}}};
    if (predictiveCache.size >= 30) predictiveCache.delete(predictiveCache.keys().next().value);
    predictiveCache.set(key, {time: Date.now(), result});
    return result;
  }
  const decisions = new Map();
  const markerSelector = '[data-centec-search-summary]';
  const contentSelector = '[data-centec-search-content]';
  const isSearch = url => url.origin === location.origin && /\/search\/?$/.test(url.pathname);
  async function fetchSection(address, query, sectionId, signal) {
    const url = new URL(address, location.href);
    url.searchParams.set('q', query);
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'last');
    url.searchParams.set('section_id', sectionId);
    url.searchParams.delete('sections');
    const response = await fetch(url, {signal});
    if (!response.ok) throw new Error('Search ' + response.status);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    const marker = doc.querySelector(markerSelector);
    if (!marker || !doc.querySelector(contentSelector) || !Number.isFinite(Number(marker.dataset.searchCount))) throw new Error('Search section missing');
    return {doc, marker, url, query, count: Number(marker.dataset.searchCount)};
  }
  async function resolveSection(address, sectionId, signal, initial) {
    const actual = new URL(address, location.href);
    const query = actual.searchParams.get('q') || '';
    const base = new URL(actual);
    [...base.searchParams.keys()].forEach(key => {if (key.startsWith('filter.') || key === 'page') base.searchParams.delete(key);});
    const constrained = base.href !== actual.href;
    const cached = decisions.get(query);
    let selected;
    if (cached && Date.now() - cached.time < 60000) return fetchSection(actual, cached.query, sectionId, signal);
    const precise = exactQuery(query);
    if (precise) {
      try {
        const match = await fetchSection(base, precise, sectionId, signal);
        if (match.count > 0) selected = match;
      } catch (error) {if (signal.aborted) throw error;}
    }
    if (!selected) {
      const original = !constrained && initial ? initial : await fetchSection(base, query, sectionId, signal);
      selected = original;
      if (original.count === 0) {
        const candidates = await Promise.allSettled(variants(query).map(candidate => fetchSection(base, candidate, sectionId, signal)));
        selected = candidates.find(item => item.status === 'fulfilled' && item.value.count > 0)?.value || original;
      }
    }
    if (signal.aborted) throw new DOMException('Aborted', 'AbortError');
    if (decisions.size >= 30) decisions.delete(decisions.keys().next().value);
    decisions.set(query, {time: Date.now(), query: selected.query});
    return constrained ? fetchSection(actual, selected.query, sectionId, signal) : selected;
  }
  let active = null;
  let currentQuery = new URL(location.href).searchParams.get('q') || '';
  function setBusy(marker, busy) {
    marker.toggleAttribute('data-search-pending', busy);
    marker.setAttribute('aria-busy', String(busy));
    const status = marker.querySelector('[data-centec-search-status]');
    if (status) {status.hidden = !busy; status.textContent = busy ? 'Buscando productos…' : '';}
  }
  function present(root, query) {
    root.querySelectorAll('input[name="q"]').forEach(input => {input.value = query; input.setAttribute('value', query);});
    const heading = root.querySelector('.centec-search-bar__title');
    if (heading) heading.textContent = 'Resultados para “' + query + '”';
    root.querySelectorAll('a[href]').forEach(link => {
      const url = new URL(link.getAttribute('href'), location.href);
      if (!isSearch(url)) return;
      url.searchParams.set('q', query);
      url.searchParams.delete('section_id');
      url.searchParams.set('type', 'product');
      link.href = url.href;
    });
  }
  async function showResults(address, mode = 'push', initial = null) {
    const marker = document.querySelector(markerSelector);
    if (!marker?.querySelector(contentSelector)) return;
    const url = new URL(address, location.href);
    if (!isSearch(url)) return;
    url.searchParams.delete('section_id');
    url.searchParams.set('type', 'product');
    url.searchParams.set('options[unavailable_products]', 'last');
    active?.abort();
    const controller = new AbortController();
    active = controller;
    document.dispatchEvent(new Event('centec:search-start'));
    const timer = setTimeout(() => controller.abort(), 10000);
    setBusy(marker, true);
    const query = url.searchParams.get('q') || '';
    try {
      const result = await resolveSection(url, marker.dataset.searchSection, controller.signal, initial);
      if (result.doc !== document) {
        await window.CentecStockOrder?.prepare(result.doc, result.url, controller.signal);
        if (controller.signal.aborted || active !== controller) return;
        const replacement = result.doc.querySelector(contentSelector);
        present(replacement, query);
        replacement.querySelectorAll('script').forEach(script => script.remove());
        const focused = document.activeElement;
        const focusId = marker.contains(focused) ? focused.id : '';
        const focusQuery = marker.contains(focused) && focused.name === 'q';
        marker.querySelector(contentSelector).replaceChildren(...replacement.childNodes);
        marker.dataset.searchCount = String(result.count);
        marker.dataset.searchPerformed = 'true';
        if (focusId) document.getElementById(focusId)?.focus({preventScroll:true});
        else if (focusQuery) marker.querySelector('input[name="q"]')?.focus({preventScroll:true});
      }
      if (controller.signal.aborted || active !== controller) return;
      currentQuery = query;
      document.querySelectorAll('[data-centec-search-input]').forEach(input => {input.value = query;});
      document.querySelectorAll('[data-centec-search-results]').forEach(results => {results.hidden = true;});
      document.querySelectorAll('[data-centec-search-input]').forEach(input => input.setAttribute('aria-expanded', 'false'));
      if (mode === 'push' && location.href !== url.href) history.pushState({centecSearch: true}, '', url);
      document.title = 'Resultados para “' + query + '” – CENTEC';
      document.querySelector('.mobile-toolbar__item--filters')?.classList.toggle('has-filters', [...url.searchParams.keys()].some(key => key.startsWith('filter.')));
    } catch (error) {
      if (active !== controller) return;
      const status = marker.querySelector('[data-centec-search-status]');
      setBusy(marker, false);
      if (status && (!controller.signal.aborted || controller.reason !== 'input')) {
        status.hidden = false;
        status.textContent = 'No pudimos actualizar la búsqueda. Vuelve a intentarlo.';
      }
    } finally {
      clearTimeout(timer);
      if (active === controller) {
        marker.removeAttribute('data-search-pending');
        marker.removeAttribute('data-search-initial');
        marker.setAttribute('aria-busy', 'false');
        const status = marker.querySelector('[data-centec-search-status]');
        if (status?.textContent === 'Buscando productos…') {status.hidden = true; status.textContent = '';}
        active = null;
      }
    }
  }
  function start() {
    const marker = document.querySelector(markerSelector);
    if (!marker?.querySelector(contentSelector)) return;
    const original = new URL(location.href);
    if (marker.dataset.searchPerformed === 'true' && (exactQuery(currentQuery) || variants(currentQuery).length)) {
      const initial = {doc: document, marker, url: original, query: currentQuery, count: Number(marker.dataset.searchCount)};
      showResults(original, 'none', initial);
    }
    document.addEventListener('submit', event => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !form.querySelector('input[name="q"]') || form.id === 'facet-filters-form') return;
      const url = new URL(form.action, location.href);
      if (!isSearch(url)) return;
      event.preventDefault();
      event.stopPropagation();
      url.search = new URLSearchParams(new FormData(form)).toString();
      showResults(url);
    }, true);
    for (const name of ['facet:criteria-changed', 'pagination:page-changed']) {
      document.addEventListener(name, event => {
        if (!marker.contains(event.target) || !event.detail?.url) return;
        const url = new URL(event.detail.url, location.href);
        if (!isSearch(url)) return;
        event.preventDefault();
        event.stopPropagation();
        url.searchParams.set('q', currentQuery);
        showResults(url);
      }, true);
    }
    document.addEventListener('click', event => {
      const link = event.target.closest?.('.centec-search-results__all');
      if (!link || event.button !== 0 || event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
      const url = new URL(link.href);
      if (!isSearch(url)) return;
      event.preventDefault();
      showResults(url);
    });
    document.addEventListener('input', event => {
      if (event.target.name !== 'q' || !active) return;
      active.reason = 'input';
      active.abort();
      setBusy(marker, false);
    });
    window.addEventListener('popstate', () => {
      if (isSearch(new URL(location.href))) showResults(location.href, 'none');
    });
  }
  window.CentecSearch = {version: 2, variants, exactQuery, exactProduct, predictive, resolveSection, showResults};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once: true});
  else start();
})();

